import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { 
  Monitor, 
  TrendingUp, 
  TrendingDown,
  MousePointerClick, 
  Users, 
  Eye,
  RefreshCw,
  ExternalLink,
  Target,
  Plus,
  Trash2,
  Loader2,
  Pencil,
  AlertCircle
} from "lucide-react";
import PageDateFilter from "@/components/PageDateFilter";
import { useBranding } from "@/contexts/BrandingContext";

const ForHeathPage = () => {
  const { sessionToken, isSuperAdmin, isAdmin, user } = useAuth();
  const { chartColors } = useBranding();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [trafficDataSource, setTrafficDataSource] = useState(null); // "ga4" or "hubspot"
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  
  // Page title from tenant settings
  const [pageTitle, setPageTitle] = useState("For Heath");

  // Custom modules state
  const [customModules, setCustomModules] = useState([]);
  const [customModuleData, setCustomModuleData] = useState({});
  const [showAddModule, setShowAddModule] = useState(false);
  const [newModule, setNewModule] = useState({
    name: "",
    page_path: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: ""
  });
  const [addingModule, setAddingModule] = useState(false);
  
  // Edit module state
  const [editingModule, setEditingModule] = useState(null);
  const [editModuleData, setEditModuleData] = useState({ name: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Listen for tenant changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== FOR HEATH: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
        // Reset data to force re-fetch
        setTrafficDataSource(null);
        setCustomModuleData({});
      }
    };
    
    const interval = setInterval(checkTenantChange, 500);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkTenantChange();
    });
    
    return () => clearInterval(interval);
  }, [currentTenantId]);

  const handleDateChange = useCallback((range) => {
    setDateRange({
      startDate: range.startDate,
      endDate: range.endDate
    });
  }, []);
  
  // Fetch tenant settings including traffic data source and page title
  // CRITICAL: Uses currentTenantId and has it as dependency
  const fetchTenantSettings = async () => {
    try {
      // Use currentTenantId from state for proper tenant isolation
      const viewingTenantId = currentTenantId;
      
      // If we have a viewing tenant ID, use the display-settings endpoint for that tenant
      let response;
      if (viewingTenantId) {
        response = await axios.get(`${API}/tenants/${viewingTenantId}/display-settings`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
      } else {
        response = await axios.get(`${API}/tenants/my-tenant-settings`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
      }
      
      // Set traffic data source (default to hubspot)
      const dataSource = response.data?.traffic_data_source || "hubspot";
      setTrafficDataSource(dataSource);
      console.log("ForHeathPage: Set traffic_data_source to", dataSource, "for tenant", viewingTenantId);
      
      // Set custom page title if configured
      const customTitle = response.data?.page_titles?.for_heath || response.data?.display_settings?.page_titles?.for_heath;
      if (customTitle) {
        setPageTitle(customTitle);
      }
    } catch (error) {
      console.error("Error fetching tenant settings:", error);
      setTrafficDataSource("hubspot"); // Default fallback
    }
  };

  // Fetch custom modules - uses currentTenantId for tenant isolation
  const fetchCustomModules = async () => {
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      
      const response = await axios.get(`${API}/integrations/ga4/custom-modules`, { params });
      setCustomModules(response.data.modules || []);
    } catch (error) {
      console.error("Error fetching custom modules:", error);
    }
  };

  // Fetch data for a custom module
  const fetchModuleData = async (moduleId) => {
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      if (dateRange.startDate) params.start_date = dateRange.startDate;
      if (dateRange.endDate) params.end_date = dateRange.endDate;

      // Use HubSpot or GA4 based on traffic data source setting
      const endpoint = trafficDataSource === "hubspot" 
        ? `${API}/integrations/hubspot/custom-module-data/${moduleId}`
        : `${API}/integrations/ga4/custom-module-data/${moduleId}`;

      const response = await axios.get(endpoint, { params });
      setCustomModuleData(prev => ({
        ...prev,
        [moduleId]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching module ${moduleId} data:`, error);
    }
  };

  // Add new custom module
  const handleAddModule = async () => {
    if (!newModule.name) {
      toast.error("Please enter a module name");
      return;
    }
    if (!newModule.page_path && !newModule.utm_source && !newModule.utm_medium && !newModule.utm_campaign) {
      toast.error("Please enter at least one filter (page path or UTM parameter)");
      return;
    }

    setAddingModule(true);
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      
      const response = await axios.post(
        `${API}/integrations/ga4/custom-modules`,
        newModule,
        { params }
      );
      
      setCustomModules(prev => [...prev, response.data.module]);
      setShowAddModule(false);
      setNewModule({ name: "", page_path: "", utm_source: "", utm_medium: "", utm_campaign: "" });
      toast.success("Module created successfully");
      
      // Fetch data for the new module
      if (response.data.module?.module_id) {
        fetchModuleData(response.data.module.module_id);
      }
    } catch (error) {
      console.error("Error creating module:", error);
      toast.error("Failed to create module");
    } finally {
      setAddingModule(false);
    }
  };

  // Delete custom module
  const handleDeleteModule = async (moduleId) => {
    if (!confirm("Are you sure you want to delete this module?")) return;

    try {
      await axios.delete(`${API}/integrations/ga4/custom-modules/${moduleId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      
      setCustomModules(prev => prev.filter(m => m.module_id !== moduleId));
      toast.success("Module deleted");
    } catch (error) {
      console.error("Error deleting module:", error);
      toast.error(error.response?.data?.detail || "Failed to delete module");
    }
  };

  // Edit module name
  const handleEditModule = async () => {
    if (!editingModule || !editModuleData.name.trim()) return;
    
    setSavingEdit(true);
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      
      await axios.patch(
        `${API}/integrations/ga4/custom-modules/${editingModule.module_id}`,
        { name: editModuleData.name.trim() },
        { params }
      );
      
      // Update local state
      setCustomModules(prev => prev.map(m => 
        m.module_id === editingModule.module_id 
          ? { ...m, name: editModuleData.name.trim() }
          : m
      ));
      
      setEditingModule(null);
      toast.success("Module name updated");
    } catch (error) {
      console.error("Error updating module:", error);
      toast.error(error.response?.data?.detail || "Failed to update module");
    } finally {
      setSavingEdit(false);
    }
  };

  // Check if current user can edit a module
  const canEditModule = (module) => {
    // Personal modules can only be edited by the creator
    if (module.scope === "personal") {
      return module.created_by === user?.email;
    }
    // Tenant-scoped modules can be edited by admins
    return isSuperAdmin || isAdmin;
  };

  const fetchData = async () => {
    if (!trafficDataSource) return; // Wait for settings to load
    
    setLoading(true);
    try {
      // Fetch data for all custom modules
      for (const module of customModules) {
        await fetchModuleData(module.module_id);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionToken) {
      fetchTenantSettings();
      fetchCustomModules();
    }
  }, [sessionToken, currentTenantId]); // CRITICAL: Added currentTenantId

  useEffect(() => {
    if (sessionToken && dateRange.startDate && trafficDataSource) {
      fetchData();
    }
  }, [sessionToken, dateRange.startDate, dateRange.endDate, customModules.length, trafficDataSource, currentTenantId]); // CRITICAL: Added currentTenantId

  // Helper function to ensure text has good contrast against background
  const getContrastTextColor = (bgColor, lightColor = "#1e293b", darkColor = "#ffffff") => {
    // If bgColor is not set or invalid, return dark color for light backgrounds
    if (!bgColor) return lightColor;
    
    // Convert hex to RGB
    const hex = bgColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return dark text for light backgrounds, light text for dark backgrounds
    return luminance > 0.5 ? lightColor : darkColor;
  };

  // High contrast colors that work well on light backgrounds
  const moduleMetricColors = [
    "#1e40af", // blue-800
    "#166534", // green-800  
    "#7c2d12", // orange-900
    "#581c87", // purple-900
  ];

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return num.toLocaleString();
  };

  // Change indicator component
  const ChangeIndicator = ({ change, inverted = false }) => {
    if (change === null || change === undefined) return null;
    const isPositive = inverted ? change < 0 : change > 0;
    const color = isPositive ? "text-green-600" : "text-red-600";
    const Icon = isPositive ? TrendingUp : TrendingDown;
    
    return (
      <div className={`flex items-center gap-1 text-xs ${color}`}>
        <Icon className="w-3 h-3" />
        <span>{Math.abs(change)}%</span>
      </div>
    );
  };

  // Check if current user can delete a module
  const canDeleteModule = (module) => {
    // Personal modules can only be deleted by the creator
    if (module.scope === "personal") {
      return module.created_by === user?.email;
    }
    
    // Tenant-scoped modules can only be deleted by admins
    return isSuperAdmin || isAdmin;
  };

  // Custom Module Card Component
  const CustomModuleCard = ({ module }) => {
    const data = customModuleData[module.module_id];
    const current = data?.current_period || {};
    const changes = data?.changes || {};
    const dataOutsideRange = data?.data_outside_range;
    
    return (
      <Card className="chart-container">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {module.name}
              {module.scope === "personal" && (
                <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Personal</span>
              )}
            </CardTitle>
            <div className="flex items-center gap-1">
              {canEditModule(module) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingModule(module);
                    setEditModuleData({ name: module.name });
                  }}
                  className="text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canDeleteModule(module) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteModule(module.module_id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <CardDescription className="text-xs">
            {module.page_path && <span className="mr-2">Path: {module.page_path}</span>}
            {module.utm_source && <span className="mr-2">Source: {module.utm_source}</span>}
            {module.utm_medium && <span className="mr-2">Medium: {module.utm_medium}</span>}
            {module.utm_campaign && <span>Campaign: {module.utm_campaign}</span>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Alert for data outside range */}
          {dataOutsideRange?.has_data && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                {dataOutsideRange.message}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-2 bg-slate-50 rounded-lg">
              <p className="text-xl font-bold" style={{ color: moduleMetricColors[0] }}>
                {formatNumber(current.sessions || 0)}
              </p>
              <p className="text-xs text-slate-500">Sessions</p>
              <ChangeIndicator change={changes.sessions} />
            </div>
            <div className="text-center p-2 bg-slate-50 rounded-lg">
              <p className="text-xl font-bold" style={{ color: moduleMetricColors[1] }}>
                {formatNumber(current.users || 0)}
              </p>
              <p className="text-xs text-slate-500">Users</p>
              <ChangeIndicator change={changes.users} />
            </div>
            <div className="text-center p-2 bg-slate-50 rounded-lg">
              <p className="text-xl font-bold" style={{ color: moduleMetricColors[2] }}>
                {formatNumber(current.pageViews || 0)}
              </p>
              <p className="text-xs text-slate-500">Page Views</p>
              <ChangeIndicator change={changes.pageViews} />
            </div>
            <div className="text-center p-2 bg-slate-50 rounded-lg">
              <p className="text-xl font-bold" style={{ color: moduleMetricColors[3] }}>
                {current.bounceRate || 0}%
              </p>
              <p className="text-xs text-slate-500">Bounce Rate</p>
              <ChangeIndicator change={changes.bounceRate} inverted />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading && customModules.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6" data-testid="for-marketing-insights-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Monitor className="w-6 h-6 text-purple-600" />
            {pageTitle}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Paid Media Analytics - Display & AdRoll Traffic
            {trafficDataSource && (
              <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded">
                via {trafficDataSource === "hubspot" ? "HubSpot" : "GA4"}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PageDateFilter 
            pageId="for-marketing-insights" 
            defaultPreset="last_30_days"
            onDateChange={handleDateChange}
          />
          <Button 
            onClick={fetchData} 
            disabled={syncing || loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {!trafficDataSource && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Monitor className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Loading traffic data source...</p>
                <p className="text-sm text-amber-600">Fetching tenant settings...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Tracking Modules - All modules are dynamically loaded from tenant settings */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-slate-600" />
            Custom Tracking Modules
          </h2>
          <Dialog open={showAddModule} onOpenChange={setShowAddModule}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1">
                <Plus className="w-4 h-4" />
                Add Module
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Custom Tracking Module</DialogTitle>
                <DialogDescription>
                  Create a custom GA4 tracking module with URL path or UTM parameters
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Module Name *</Label>
                  <Input
                    placeholder="e.g., Blog Campaign Traffic"
                    value={newModule.name}
                    onChange={(e) => setNewModule(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Page Path (optional)</Label>
                  <Input
                    placeholder="e.g., /landing-page or /blog"
                    value={newModule.page_path}
                    onChange={(e) => setNewModule(prev => ({ ...prev, page_path: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">Filter by URL path containing this value</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>UTM Source</Label>
                    <Input
                      placeholder="e.g., google"
                      value={newModule.utm_source}
                      onChange={(e) => setNewModule(prev => ({ ...prev, utm_source: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>UTM Medium</Label>
                    <Input
                      placeholder="e.g., cpc"
                      value={newModule.utm_medium}
                      onChange={(e) => setNewModule(prev => ({ ...prev, utm_medium: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>UTM Campaign</Label>
                    <Input
                      placeholder="e.g., spring_sale"
                      value={newModule.utm_campaign}
                      onChange={(e) => setNewModule(prev => ({ ...prev, utm_campaign: e.target.value }))}
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleAddModule} 
                  disabled={addingModule}
                  className="w-full"
                >
                  {addingModule ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Module"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Module Dialog */}
          <Dialog open={!!editingModule} onOpenChange={(open) => !open && setEditingModule(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Module Name</DialogTitle>
                <DialogDescription>
                  Update the display name for this tracking module
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit-module-name">Module Name</Label>
                  <Input
                    id="edit-module-name"
                    value={editModuleData.name}
                    onChange={(e) => setEditModuleData({ ...editModuleData, name: e.target.value })}
                    placeholder="e.g., Display Campaign Q1"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEditingModule(null)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleEditModule}
                    disabled={savingEdit || !editModuleData.name.trim()}
                  >
                    {savingEdit ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {customModules.length === 0 ? (
          <Card className="bg-slate-50 border-dashed">
            <CardContent className="py-8 text-center">
              <p className="text-slate-500 mb-2">No custom modules yet</p>
              <p className="text-sm text-slate-400">Click "Add Module" to track specific landing pages or UTM campaigns</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {customModules.map((module) => (
              <CustomModuleCard key={module.module_id} module={module} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ForHeathPage;
